import { IconButton, Stack, Typography, useTheme } from "@mui/material";
import {
  AttachIcon,
  CircularLoader,
  InfoIcon,
  PlaneFilledIcon,
  Tooltip,
} from "components";
import { useGlobalStore } from "controller/store/globalStore";
import { useAtomValue, useSetAtom } from "jotai";
import {
  memo,
  type Ref,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import toast from "react-hot-toast";
import { ChatAttachmentPreview } from "shared/features/chat/ChatAttachmentPreview";
import { useFileAttachmentUpload } from "shared/features/chat/hooks/useFileAttachmentUpload";
import { ReplyPreview } from "shared/features/chat/ReplyPreview";
import {
  chatFileAttachments,
  chatHasErrorFiles,
  chatHasUploadingFiles,
  chatIsUploadingImages,
  chatMembers,
  chatReplyToMessage,
  chatSendMode,
  clearChatFileAttachments,
  clearChatReplyToMessage,
  removeChatFileAttachment,
  setChatSendMode,
  setChatUploadingImages,
  setModeBannerVisible,
} from "shared/features/chat/store";
import {
  type ChatMessageInputRef,
  getPlaceholderText,
  TipTapChatInput,
} from "shared/features/chat/TipTapChatInput";
import { stripHtml } from "shared/features/chat/utils/stripHtml";
import {
  DictationMicButton,
  DictationView,
  dictationIssueMessage,
  tiptapTarget,
  useEditorDictation,
} from "shared/features/voice-dictation";
import type { AccessSpecifier, AttachmentResultType } from "shared/types";
import { AccessSpecifierMenu } from "./AccessSpecifierMenu";

// ─── Types ───────────────────────────────────────────────────────────

interface PinnedChatInputSectionProps {
  serviceType: "ticket" | "task";
  serviceId: string;
  isDisabled: boolean;
  isSendingMessage: boolean;
  editorRef: RefObject<ChatMessageInputRef | null>;
  sendMode?: AccessSpecifier;
  onModeChange?: (mode: AccessSpecifier) => void;
  onSend: (params: {
    messageText: string;
    attachmentArray?: Array<{
      mapping_id: string;
      file_type: string;
      url: string;
      original_name: string;
    }>;
    mentionedUsers?: string[];
    mentionedEmails?: string[];
    parentMessageId?: string;
    excerpt?: string;
  }) => void;
}

// ─── Component ───────────────────────────────────────────────────────

export const PinnedChatInputSection = memo(function PinnedChatInputSection({
  serviceType,
  serviceId,
  isDisabled,
  isSendingMessage,
  editorRef,
  sendMode,
  onModeChange,
  onSend,
}: PinnedChatInputSectionProps) {
  const theme = useTheme();
  const [isEditorEmpty, setIsEditorEmpty] = useState(true);

  // ─── Jotai Atoms ──────────────────────────────────────────────
  const sendMessageAccessSpecifier = useAtomValue(chatSendMode);
  const setSendMode = useSetAtom(setChatSendMode);
  const isUploadingImages = useAtomValue(chatIsUploadingImages);
  const setUploading = useSetAtom(setChatUploadingImages);
  const setBannerVisible = useSetAtom(setModeBannerVisible);
  const members = useAtomValue(chatMembers);
  const replyToMessage = useAtomValue(chatReplyToMessage);
  const clearReply = useSetAtom(clearChatReplyToMessage);

  // ─── File Attachment Atoms ────────────────────────────────────
  const fileAttachments = useAtomValue(chatFileAttachments);
  const hasUploadingFiles = useAtomValue(chatHasUploadingFiles);
  const hasErrorFiles = useAtomValue(chatHasErrorFiles);
  const removeFile = useSetAtom(removeChatFileAttachment);
  const clearFiles = useSetAtom(clearChatFileAttachments);

  // ─── File Upload Hook ─────────────────────────────────────────
  const { fileInputRef, openFilePicker, handleInputChange } =
    useFileAttachmentUpload({
      serviceId,
      serviceType,
      isDisabled,
    });

  // ─── Effective mode (thread override or global atom) ──────────
  const effectiveMode = sendMode ?? sendMessageAccessSpecifier;

  // ─── Banner background per access specifier ──────────────────
  const modeBannerBgColor: Record<AccessSpecifier, string> = {
    public: theme.palette.primitive.system.violet[50] as string,
    private: theme.palette.primitive.system.blue[50] as string,
    self: theme.palette.primitive.neutrals.neutral[25] as string,
  };

  const bannerTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showModeBanner = useCallback(() => {
    setBannerVisible(true);
    if (bannerTimeoutRef.current) {
      clearTimeout(bannerTimeoutRef.current);
    }
    bannerTimeoutRef.current = setTimeout(() => {
      setBannerVisible(false);
    }, 3000);
  }, [setBannerVisible]);

  // ─── Mode change handler ──────────────────────────────────────
  const handleModeChange = useCallback(
    (mode: AccessSpecifier) => {
      if (onModeChange) {
        onModeChange(mode);
      } else {
        setSendMode(mode);
      }
      showModeBanner();
    },
    [onModeChange, setSendMode, showModeBanner],
  );

  // ─── Merge file attachments into send payload ─────────────────
  const handleSendWithAttachments = useCallback(
    (params: {
      messageText: string;
      attachmentArray: Array<AttachmentResultType>;
      mentionedUsers?: string[];
      mentionedEmails?: string[];
    }) => {
      const completedFileAttachments = fileAttachments
        .filter((f) => f.status === "Uploaded")
        .map((f) => f.responseData);

      const mergedAttachments = [
        ...(params.attachmentArray ?? []),
        ...completedFileAttachments,
      ];

      const replyData = replyToMessage
        ? {
            parentMessageId: replyToMessage.chatId,
            excerpt: stripHtml(replyToMessage.message).slice(0, 500),
          }
        : undefined;

      onSend({
        messageText: params.messageText,
        attachmentArray:
          mergedAttachments.length > 0 ? mergedAttachments : undefined,
        mentionedUsers: params.mentionedUsers,
        mentionedEmails: params.mentionedEmails,
        ...(replyData && {
          parentMessageId: replyData.parentMessageId,
          excerpt: replyData.excerpt,
        }),
      });

      clearFiles();
    },
    [onSend, fileAttachments, clearFiles, replyToMessage],
  );

  // ─── Voice Dictation ──────────────────────────────────────────
  // Same flow as the ticket chat composer. Pinned chat keeps no draft, so
  // words still being dictated when the panel closes are dropped (the
  // recording is still saved) — product call, 2026-10-19.
  const timezone = useGlobalStore((state) => state.timezone);
  const dictation = useEditorDictation({
    getTarget: () => tiptapTarget(editorRef.current?.getEditor()),
    disabled: isDisabled,
  });
  const {
    issue: dictationIssue,
    isDictating,
    dismissIssue: dismissDictationIssue,
  } = dictation;

  // Dictation that couldn't start (or ended before any words) is back to the
  // composer, so the reason goes in a toast. Words kept show it in the box.
  useEffect(() => {
    if (!dictationIssue || isDictating) return;
    toast.error(dictationIssueMessage(dictationIssue, timezone));
    dismissDictationIssue();
  }, [dictationIssue, isDictating, dismissDictationIssue, timezone]);

  const hasUploadedFiles = fileAttachments.some((f) => f.status === "Uploaded");
  const isSendDisabled =
    isDisabled ||
    isSendingMessage ||
    isUploadingImages ||
    hasUploadingFiles ||
    hasErrorFiles ||
    (isEditorEmpty && !hasUploadedFiles);

  return (
    <Stack
      sx={{
        backgroundColor: theme.palette.neutral.main,
        px: "8px",
        borderTop: 0,
        pb: "4px",
      }}
    >
      <Stack
        ref={dictation.scopeRef}
        {...dictation.scopeProps}
        sx={{
          width: "100%",
          borderRadius: "8px",
          border: `1px solid ${theme.palette.neutral["015"]}`,
          boxShadow: "0px 0px 4px 0px #00000029, 0px 0px 2px 0px #00000014",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* ─── Disabled banner ──────────────────────────────────── */}
        {isDisabled && (
          <Stack
            direction="row"
            sx={{
              width: "100%",
              gap: "4px",
              alignItems: "center",
              px: "8px",
              py: "4px",
              bgcolor: theme.palette.secondary["purple-tint-3"],
              borderRadius: "8px 8px 0 0",
            }}
          >
            <InfoIcon sx={{ width: "14px", height: "14px" }} />
            <Typography variant="body-small">Chat is disabled</Typography>
          </Stack>
        )}

        {/* ─── Access Specifier mode banner ─────────────────────── */}
        {!isDisabled && serviceType === "ticket" && (
          <Stack
            sx={{
              width: "100%",
              px: "8px",
              py: "6px",
              bgcolor: modeBannerBgColor[effectiveMode],
              borderRadius: "8px 8px 0 0",
            }}
          >
            <Typography
              variant="body-small"
              sx={{ color: theme.palette.lightBg.high }}
            >
              {getPlaceholderText(effectiveMode)}
            </Typography>
          </Stack>
        )}

        {/* ─── Uploading images banner ──────────────────────────── */}
        {isUploadingImages && (
          <Stack
            direction="row"
            sx={{
              width: "100%",
              gap: "4px",
              alignItems: "center",
              px: "8px",
              py: "4px",
              bgcolor: theme.palette.secondary["purple-tint-3"],
            }}
          >
            <CircularLoader size={14} thickness={4} />
            <Typography variant="body-small">Uploading images...</Typography>
          </Stack>
        )}

        {/* ─── Reply Preview ──────────────────────────────────── */}
        {replyToMessage && !isDisabled && (
          <ReplyPreview
            senderDisplayName={replyToMessage.senderDisplayName}
            messageHtml={replyToMessage.message}
            onClose={() => clearReply()}
          />
        )}

        {/* ─── Compact Input Row ─────────────────────────────── */}
        {/* Hidden, not removed, while dictating: the editor keeps the text and
            the caret ✓ inserts at. */}
        <Stack
          direction="row"
          sx={{
            display: isDictating ? "none" : "flex",
            alignItems: "center",
            gap: "4px",
            px: "8px",
            py: "4px",
          }}
        >
          {/* Access Specifier inline menu */}
          {serviceType === "ticket" && (
            <>
              <AccessSpecifierMenu
                activeMode={effectiveMode}
                onModeChange={handleModeChange}
                isDisabled={isDisabled}
              />
              <Typography
                variant="body-medium"
                sx={{
                  color: theme.palette.lightBg.low,
                  flexShrink: 0,
                  lineHeight: 1,
                }}
              >
                :
              </Typography>
            </>
          )}

          {/* TipTap Editor */}
          <Stack
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              backgroundColor: theme.palette.neutral.main,
              borderRadius: "4px",
              "& .tiptap": {
                padding: "4px !important",
                minHeight: "24px !important",
              },
            }}
          >
            <TipTapChatInput
              ref={editorRef as Ref<ChatMessageInputRef>}
              serviceId={serviceId}
              serviceType={serviceType}
              sendMessageAccessSpecifier={effectiveMode}
              isSendingMessage={isSendingMessage}
              isFileUploading={hasUploadingFiles || hasErrorFiles}
              isDisabled={isDisabled}
              members={members}
              isPinnedChat
              onSend={handleSendWithAttachments}
              toggleUploadingStatus={setUploading}
              onContentChange={setIsEditorEmpty}
            />
          </Stack>

          {/* Attach icon */}
          <Tooltip title="Attachment">
            <IconButton
              size="small"
              onClick={() => !isDisabled && openFilePicker()}
              disabled={isDisabled}
              aria-label="Attach files"
              sx={{ flexShrink: 0, p: "4px" }}
            >
              <AttachIcon
                sx={{ width: 20, height: 20 }}
                htmlColor={
                  isDisabled
                    ? theme.palette.neutral["020"]
                    : theme.palette.lightBg.low
                }
              />
            </IconButton>
          </Tooltip>

          {dictation.canDictate && (
            <DictationMicButton
              onClick={dictation.start}
              sx={{ flexShrink: 0, p: "4px" }}
            />
          )}

          {/* Send icon */}
          <IconButton
            size="small"
            onClick={() => {
              if (!isSendDisabled) {
                editorRef.current?.handleExternalSend();
              }
            }}
            disabled={isSendDisabled}
            aria-label="Send message"
            sx={{ flexShrink: 0, p: "4px" }}
          >
            <PlaneFilledIcon
              sx={{ width: 20, height: 20 }}
              htmlColor={
                isSendDisabled
                  ? theme.palette.neutral["020"]
                  : theme.palette.primary.main
              }
            />
          </IconButton>
        </Stack>

        {/* ─── Dictation (in place of the input row) ─────────── */}
        {isDictating && (
          <Stack sx={{ gap: "8px", px: "8px", py: "4px" }}>
            <DictationView {...dictation.viewProps} />
          </Stack>
        )}

        {/* ─── File Attachment Preview ───────────────────────── */}
        <ChatAttachmentPreview
          files={fileAttachments}
          onRemove={(key) => removeFile(key)}
          onClearAll={() => clearFiles()}
        />
      </Stack>

      {/* ─── Hidden file input ──────────────────────────────── */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".png,.jpg,.jpeg,.gif,.webp,.svg,.bmp,.doc,.docx,.pdf,.txt,.md,.rtf,.xls,.xlsx,.csv,.ppt,.pptx,.zip,.rar,.7z,.json"
        style={{ display: "none" }}
        onChange={handleInputChange}
        aria-label="Attach files"
      />
    </Stack>
  );
});
